import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import EventLabel from '../../components/EventLabel/EventLabel';
import _, { set } from 'lodash';

function PersonDetailsSummary(props) {
  const { showPersonDetails = true, showCompanyDetails = true, showInvoiceDetails = true, showQuestionDetails = true } = props.settings;
  return (
    <>
      {showPersonDetails ? <PersonDetails {...props} /> : null}
      {showCompanyDetails ? <CompanyDetails {...props} /> : null}
      {showInvoiceDetails ? <InvoiceDetails {...props} /> : null}
      {showQuestionDetails ? <QuestionDetails {...props} /> : null}
    </>
  );
}

export default observer(PersonDetailsSummary);

const CountryChoice = ({ countries, value, key, environment }) => {
  const country = countries.find(c => c.code.toUpperCase() === value.toUpperCase());

  if (!country) return null;
  if (environment.lang === 'swe') {
    return <>{country.nameSwe}</>;
  }
  return <>{country.nameInt}</>;
};

const InvoiceDetails = ({ order, countries, environment, orders, validations }) => {
  if ((!order || (order && !order.invoice) || !order?.invoice?.orgNo || !order?.invoice?.name || !order.invoice.countryId) && !order?.invoice?.vatNo)
    return null;

  return (
    <>
      <label className="details-title" style={{ textAlign: 'left', width: '100%' }}>
        <EventLabel name={'Invoice'} groupName={'Content.StaticLabels'} />
      </label>
      <div className="detail-list">
        <>
          {Object.entries(order.invoice).map(oi => {
            if (!oi[1]) return null;
            if (oi[0] == 'companyId' || oi[0] == 'countryId' || oi[0] == 'gln') return null;
            const label = `${oi[0]}`;
            if (label == 'inExchangeRecipentId' || label == 'electronicInvoice') return null;
            const value = label === 'countryCode' ? <CountryChoice {...{ countries, environment, value: oi[1] }} /> : oi[1];
            return (
              <Grid className="detail-item" key={v4()} container>
                <Grid item xs style={{ textAlign: 'left' }}>
                  {label && (
                    <p>
                      <EventLabel name={label} groupName={'Content.StaticLabels'} />
                    </p>
                  )}
                </Grid>
                <Grid item xs style={{ textAlign: 'left' }}>
                  <p>{value}</p>
                </Grid>
              </Grid>
            );
          })}
        </>
      </div>
      <hr />
    </>
  );
};

const PersonDetails = ({ order, countries, environment, fields }) => {
  const fieldList = useMemo(() => {
    return fields.filter(f => f.type === 'Person').sort((a, b) => a.sortOrder - b.sortOrder);
  }, [fields]);

  if (!order || (order && !order.person) || Object.entries(order?.person)?.length === 0 || !Object.entries(order?.person ?? {})?.some(([_, value]) => value))
    return null;
  return (
    <>
      <label className="details-title" style={{ textAlign: 'left', width: '100%' }}>
        <EventLabel name={'PersonDetails'} groupName={'Content.StaticLabels'} />
      </label>
      <div className="detail-list">
        {fieldList?.map(field => {
          const label = field?.fieldLanguage.find(fl => fl.languageCode === environment.lang)?.label || `${field.name}`;
          let value = order?.person[_.camelCase(field?.name)];

          if (field.name == 'CountryId') {
            const country = countries.find(c => c.countryId === value);
            value = environment.lang === 'swe' ? country?.nameSwe : country?.nameInt;
          }

          if (!value) return null;

          return (
            <Grid className="detail-item" key={v4()} container>
              <Grid item xs style={{ textAlign: 'left' }}>
                <p>{label}</p>
              </Grid>
              <Grid item xs style={{ textAlign: 'left' }}>
                <p>{value}</p>
              </Grid>
            </Grid>
          );
        })}
      </div>
      <hr style={{ width: 'unset' }} />
    </>
  );
};

const CompanyDetails = ({ order, fields, environment, countries }) => {
  const fieldList = useMemo(() => {
    return fields.filter(f => f.type === 'Company').sort((a, b) => a.sortOrder - b.sortOrder);
  }, [fields]);

  if (
    !order ||
    (order && !order.company) ||
    Object.entries(order?.company)?.length === 0 ||
    Object.entries(order?.company ?? {})
      ?.filter(([key, _]) => key !== 'companyId')
      ?.every(([_, value]) => value === '' || value === null)
  ) {
    return null;
  }

  return (
    <>
      <label className="details-title" style={{ textAlign: 'left', width: '100%' }}>
        <EventLabel name={'CompanyDetails'} groupName={'Content.StaticLabels'} />
      </label>
      <div className="detail-list">
        {fieldList?.map(field => {
          const label = field?.fieldLanguage.find(fl => fl.languageCode === environment.lang)?.label || `${field.name}`;
          const value = order?.company[_.camelCase(field?.name)];

          if (!value) return null;

          return (
            <Grid className="detail-item" key={v4()} container>
              <Grid item xs style={{ textAlign: 'left' }}>
                <p>{label}</p>
              </Grid>
              <Grid item xs style={{ textAlign: 'left' }}>
                <p>{value}</p>
              </Grid>
            </Grid>
          );
        })}
        {order?.company?.vatNo && (
          <Grid className="detail-item" key={v4()} container>
            <Grid item xs style={{ textAlign: 'left' }}>
              <EventLabel name={'VatNo'} groupName={'Content.StaticLabels'} />
            </Grid>
            <Grid item xs style={{ textAlign: 'left' }}>
              <p>{order?.company?.vatNo}</p>
            </Grid>
          </Grid>
        )}
      </div>
      <hr style={{ width: 'unset' }} />
    </>
  );
};

const QuestionDetails = props => {
  const { order, questions, environment, conditions } = props;

  const showQuestionModule = id => {
    let foundModule = true;
    const specificModule = props.contentLayout.contentLayoutModules.find(x => x.contentModule.type == 'QUESTION' && x.contentModule.settings.questionId == id);
    if (specificModule) {
      const isHidden = conditions[specificModule.contentLayoutModuleId]?.hidden;
      if (isHidden) {
        foundModule = false;
      }
    }
    return foundModule;
  };

  const questionList = useMemo(() => Array.from(questions).sort((a, b) => a.sortOrder - b.sortOrder), [questions, order?.answer]);

  if (
    !order ||
    (order && !order.answer) ||
    (!order?.answer?.some(({ answerRow }) => answerRow?.length > 0) && !order?.orderExtra?.find(oe => oe.name === 'Matchmaking'))
  ) {
    return null;
  }


  return (
    <>
      <label className="details-title" style={{ textAlign: 'left', width: '100%' }}>
        <EventLabel name={'Answers'} groupName={'Content.StaticLabels'} />
      </label>
      <div className="detail-list">
        {questionList.map(question => {
          const answer = order.answer.find(a => a.questionId == question.questionId);
          let show = showQuestionModule(question.questionId);
          if (!answer || !show) return null;
          const questionName = question?.questionLanguage?.find(fl => fl.languageCode == environment.lang)?.label;
          if (answer.answerRow.length > 0) {
            if (question.type == 'Option') {
              return (
                <Grid className="detail-item" key={v4()} container>
                  <Grid item xs style={{ textAlign: 'left' }}>
                    <p>{questionName}</p>
                  </Grid>
                  <Grid item xs style={{ textAlign: 'left' }}>
                    {answer.answerRow.map(o => {
                      return (
                        <p key={v4()} style={{ textAlign: 'left' }}>
                          {
                            question.questionOption
                              ?.find(qo => qo.questionOptionId == o.questionOptionId)
                              ?.questionOptionLanguage?.find(fl => fl.languageCode == environment.lang)?.label
                          }
                        </p>
                      );
                    })}
                  </Grid>
                </Grid>
              );
            } else {
              return (
                <Grid className="detail-item" key={v4()} container>
                  <Grid item xs style={{ textAlign: 'left' }}>
                    <p>{questionName}</p>
                  </Grid>
                  <Grid item xs style={{ textAlign: 'left' }}>
                    <p>{answer.answerRow[0].text}</p>
                  </Grid>
                </Grid>
              );
            }
          }
        })}
        {order?.orderExtra?.find(oe => oe.name === 'Matchmaking') && (
          <Grid className="detail-item" key={v4()} container>
            <Grid item xs style={{ textAlign: 'left' }}>
              <p>
                <EventLabel name="MatchMakingAgreement" groupName={'Content.StaticLabels'} />
              </p>
            </Grid>
            <Grid item xs style={{ textAlign: 'left' }}>
              {order?.orderExtra?.find(oe => oe.name === 'Matchmaking')?.value.toLowerCase() === 'true' ? (
                <EventLabel name="Accepted" groupName={'Content.StaticLabels'} />
              ) : (
                <EventLabel name="NotAccepted" groupName={'Content.StaticLabels'} />
              )}
            </Grid>
          </Grid>
        )}
      </div>
    </>
  );
};
